'use client';

import { useEffect, useState } from 'react';
import { flipSlideHorizontally } from '@/lib/deck/block-helpers';
import type { Anchor, Block, ScrimDirection } from '@/lib/deck/blocks';
import { ANCHORS } from '@/lib/deck/blocks';
import type { BlockSlide, TextColor } from '@/lib/deck/types';
import { BackgroundAdjustControls } from './BackgroundAdjustControls';
import { PhotoslopLibraryPanel } from './PhotoslopLibraryPanel';
import { GeneratePanel } from './GeneratePanel';

type PanelView = 'slide' | 'background';
type BgTab = 'library' | 'upload' | 'generate';

const FIELD = 'font-dank-mono text-[10px] uppercase tracking-widest text-slate-400';
const BTN =
  'rounded-full border border-beige px-3 py-1 font-inter text-xs text-slate-600 hover:border-marine-green hover:text-marine-green';

const BLOCK_PALETTE: { name: string; value: string }[] = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Ink', value: '#2E3245' },
  { name: 'Cream', value: '#FCF7F1' },
  { name: 'Marine green', value: '#1AC799' },
  { name: 'Coral', value: '#F16764' },
  { name: 'Cyan', value: '#28CCDD' },
  { name: 'Purple', value: '#9B7EDE' },
  { name: 'Orange', value: '#E8A55C' },
];

/** Default scrim applied when the user switches the gradient on. */
const DEFAULT_SCRIM = {
  direction: 'right' as ScrimDirection,
  stops: [
    { at: 0, opacity: 0.6 },
    { at: 55, opacity: 0.25 },
    { at: 100, opacity: 0.0 },
  ],
};

function peakOpacity(stops: { at: number; opacity: number }[]): number {
  return stops.reduce((max, s) => Math.max(max, s.opacity), 0);
}

function scaleStops(
  stops: { at: number; opacity: number }[],
  next: number,
): { at: number; opacity: number }[] {
  const cur = peakOpacity(stops);
  if (cur === 0) {
    return DEFAULT_SCRIM.stops.map((s) => ({
      ...s,
      opacity: +(s.opacity * (next / (peakOpacity(DEFAULT_SCRIM.stops) || 1))).toFixed(3),
    }));
  }
  const ratio = next / cur;
  return stops.map((s) => ({ ...s, opacity: +Math.min(1, s.opacity * ratio).toFixed(3) }));
}

const DIR_GRID: (ScrimDirection | null)[][] = [
  ['top-left', 'top', 'top-right'],
  ['left', null, 'right'],
  ['bottom-left', 'bottom', 'bottom-right'],
];

const DIR_ARROW: Record<ScrimDirection, string> = {
  'top-left': '↖',
  top: '↑',
  'top-right': '↗',
  left: '←',
  right: '→',
  'bottom-left': '↙',
  bottom: '↓',
  'bottom-right': '↘',
};

interface BlockInspectorProps {
  slide: BlockSlide;
  onChange: (slide: BlockSlide) => void;
  onPickBackground: (src: string) => void;
  onDuplicateSlide: () => void;
  onDeleteSlide: () => void;
  // Block-level context
  selectedBlock?: Block | null;
  onSetAnchor?: (anchor: Anchor) => void;
  onReorderUp?: () => void;
  onReorderDown?: () => void;
  onAdjustWidth?: (delta: number) => void;
  onAdjustFontSize?: (delta: number) => void;
  /** Set exact px, or null to clear (use design-token size) */
  onSetFontSize?: (px: number | null) => void;
  onColor?: (color: string | null) => void;
  /** Paragraph alignment inside the block width; `left` clears stored `textAlign` (default). */
  onSetTextAlign?: (align: 'left' | 'center' | 'right') => void;
  onDuplicateBlock?: () => void;
  onDeleteBlock?: () => void;
  onDeselectBlock?: () => void;
  /** Increment (e.g. from parent) to jump the right panel to Background options. */
  openBackgroundNonce?: number;
}

export function BlockInspector({
  slide,
  onChange,
  onPickBackground,
  onDuplicateSlide,
  onDeleteSlide,
  selectedBlock = null,
  onSetAnchor,
  onReorderUp,
  onReorderDown,
  onAdjustWidth,
  onAdjustFontSize,
  onSetFontSize,
  onColor,
  onSetTextAlign,
  onDuplicateBlock,
  onDeleteBlock,
  onDeselectBlock,
  openBackgroundNonce = 0,
}: BlockInspectorProps) {
  const [view, setView] = useState<PanelView>('slide');
  const [bgTab, setBgTab] = useState<BgTab>('library');

  useEffect(() => {
    if (openBackgroundNonce > 0) {
      setView('background');
    }
  }, [openBackgroundNonce]);

  const flipSlide = () => onChange(flipSlideHorizontally(slide));

  const scrimOn = !!slide.scrim;
  const scrim = slide.scrim ?? DEFAULT_SCRIM;
  const intensity = Math.round(peakOpacity(scrim.stops) * 100);

  const toggleScrim = () => {
    if (scrimOn) {
      onChange({ ...slide, scrim: undefined });
    } else {
      onChange({ ...slide, scrim: { ...DEFAULT_SCRIM } });
    }
  };

  const setDirection = (dir: ScrimDirection) => {
    onChange({ ...slide, scrim: { ...scrim, direction: dir } });
  };

  const setIntensity = (pct: number) => {
    const next = pct / 100;
    onChange({ ...slide, scrim: { ...scrim, stops: scaleStops(scrim.stops, next) } });
  };

  const bgOpacityPct = Math.round((slide.bg?.opacity ?? 1) * 100);

  const setBgOpacity = (pct: number) => {
    const v = pct / 100;
    const nextBg = { ...(slide.bg ?? {}) };
    if (v >= 1) {
      delete nextBg.opacity;
    } else {
      nextBg.opacity = Math.max(0, Math.min(1, v));
    }
    onChange({ ...slide, bg: nextBg });
  };

  const pickBg = (src: string) => {
    onPickBackground(src);
    setView('slide');
  };

  // ── Block view (when a block is selected on the canvas) ──────────────────
  if (selectedBlock) {
    const isText =
      selectedBlock.kind !== 'qr' &&
      selectedBlock.kind !== 'logo' &&
      selectedBlock.kind !== 'bullets' &&
      selectedBlock.kind !== 'conversation';
    const supportsFontSize =
      selectedBlock.kind !== 'qr' && selectedBlock.kind !== 'logo';
    const isConversation = selectedBlock.kind === 'conversation';
    const hasWidth = 'w' in selectedBlock;
    const blockWithColor = selectedBlock as Block & { color?: string; fontSize?: number };
    const conversationSizePx = isConversation ? (blockWithColor.fontSize ?? 16) : null;

    return (
      <div className="flex h-full flex-col overflow-hidden">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 border-b border-beige bg-white px-4 py-3">
          <button
            type="button"
            onClick={onDeselectBlock}
            className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-marine-green"
          >
            ← Slide
          </button>
          <span className="font-dank-mono text-[10px] text-slate-300">/</span>
          <span className="truncate font-dank-mono text-[10px] uppercase tracking-widest text-slate-700">
            {selectedBlock.kind}
          </span>
        </div>

        <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
          {/* Kind + anchor label */}
          <div className="rounded-xl border border-beige bg-white p-4">
            <p className={FIELD}>{selectedBlock.kind} · {selectedBlock.anchor}</p>
          </div>

          {/* Position — 3×3 anchor grid */}
          <div className="space-y-3 rounded-xl border border-beige bg-white p-4">
            <p className={FIELD}>Position</p>
            <div
              className="grid gap-1 rounded-lg border border-beige bg-slate-50 p-2"
              style={{ gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, 1fr)' }}
            >
              {ANCHORS.map((a) => {
                const active = a === selectedBlock.anchor;
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => onSetAnchor?.(a)}
                    title={a}
                    className={`flex aspect-square items-center justify-center rounded transition ${
                      active ? 'bg-marine-green' : 'bg-white hover:bg-marine-green/20'
                    }`}
                  >
                    <span
                      className={`block h-1.5 w-1.5 rounded-full ${
                        active ? 'bg-white' : 'bg-slate-300'
                      }`}
                    />
                  </button>
                );
              })}
            </div>
            {/* Stack reorder */}
            <div className="flex gap-2">
              <button type="button" onClick={onReorderUp} className={`${BTN} flex-1`} title="Move up in stack">
                ↑ Up
              </button>
              <button type="button" onClick={onReorderDown} className={`${BTN} flex-1`} title="Move down in stack">
                ↓ Down
              </button>
            </div>
          </div>

          {/* Text alignment (text blocks only) */}
          {isText && (
            <div className="space-y-3 rounded-xl border border-beige bg-white p-4">
              <p className={FIELD}>Align in block</p>
              <div className="flex gap-1">
                {(['left', 'center', 'right'] as const).map((align) => {
                  const effective =
                    (selectedBlock as { textAlign?: 'left' | 'center' | 'right' }).textAlign ??
                    'left';
                  const active = effective === align;
                  return (
                    <button
                      key={align}
                      type="button"
                      onClick={() => onSetTextAlign?.(align)}
                      className={`flex-1 rounded-lg border px-2 py-1.5 font-inter text-[11px] font-semibold capitalize transition ${
                        active
                          ? 'border-marine-green bg-marine-green text-white'
                          : 'border-beige text-slate-600 hover:border-marine-green/50'
                      }`}
                    >
                      {align}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Size controls */}
          {(hasWidth || supportsFontSize) && (
            <div className="space-y-3 rounded-xl border border-beige bg-white p-4">
              <p className={FIELD}>Size</p>
              {hasWidth && (
                <div>
                  <p className="mb-1.5 font-inter text-[10px] text-slate-400">Width</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => onAdjustWidth?.(-4)} className={`${BTN} flex-1`}>
                      W−
                    </button>
                    <button type="button" onClick={() => onAdjustWidth?.(4)} className={`${BTN} flex-1`}>
                      W+
                    </button>
                  </div>
                </div>
              )}
              {supportsFontSize && (
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <p className="font-inter text-[10px] text-slate-400">
                      {isConversation ? 'Bubble size' : 'Font size (px)'}
                    </p>
                    <span className="font-dank-mono text-[10px] text-slate-500">
                      {isConversation
                        ? `${conversationSizePx}px`
                        : blockWithColor.fontSize != null
                          ? `${blockWithColor.fontSize}px`
                          : 'auto'}
                    </span>
                  </div>
                  {isConversation && conversationSizePx != null && (
                    <input
                      type="range"
                      min={12}
                      max={40}
                      step={1}
                      value={conversationSizePx}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isNaN(n)) onSetFontSize?.(n);
                      }}
                      className="mb-2 w-full accent-marine-green"
                      aria-label="Bubble size"
                    />
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="number"
                      min={isConversation ? 12 : 10}
                      max={isConversation ? 40 : 120}
                      step={1}
                      placeholder={isConversation ? '16' : 'Auto'}
                      value={
                        isConversation
                          ? String(conversationSizePx ?? 16)
                          : blockWithColor.fontSize != null
                            ? String(blockWithColor.fontSize)
                            : ''
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          onSetFontSize?.(null);
                          return;
                        }
                        const n = parseInt(raw, 10);
                        if (!Number.isNaN(n)) onSetFontSize?.(n);
                      }}
                      className="min-w-[4.5rem] flex-1 rounded-md border border-beige bg-white px-2 py-1.5 font-inter text-sm text-slate-800 tabular-nums"
                    />
                    <button
                      type="button"
                      onClick={() => onAdjustFontSize?.(isConversation ? -1 : -2)}
                      className={`${BTN} shrink-0`}
                    >
                      {isConversation ? '−' : 'A−'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onAdjustFontSize?.(isConversation ? 1 : 2)}
                      className={`${BTN} shrink-0`}
                    >
                      {isConversation ? '+' : 'A+'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onSetFontSize?.(null)}
                      className="shrink-0 rounded-full border border-beige px-2.5 py-1 font-inter text-[11px] text-slate-500 hover:border-marine-green hover:text-marine-green"
                      title={
                        isConversation
                          ? 'Reset to default bubble size (16px)'
                          : 'Use default size from layer type'
                      }
                    >
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Colour */}
          {isText && (
            <div className="space-y-3 rounded-xl border border-beige bg-white p-4">
              <p className={FIELD}>Colour</p>
              <div className="flex flex-wrap gap-2">
                {BLOCK_PALETTE.map((c) => {
                  const active =
                    blockWithColor.color?.toLowerCase() === c.value.toLowerCase();
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => onColor?.(c.value)}
                      title={c.name}
                      className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                        active
                          ? 'border-marine-green ring-2 ring-marine-green/30'
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: c.value, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)' }}
                    />
                  );
                })}
                <button
                  type="button"
                  onClick={() => onColor?.(null)}
                  className="rounded-full border border-beige px-2.5 py-0.5 font-inter text-[11px] text-slate-500 hover:border-marine-green hover:text-marine-green"
                >
                  clear
                </button>
              </div>
            </div>
          )}

          {/* Block actions */}
          <div className="space-y-2 rounded-xl border border-beige bg-white p-4">
            <p className={FIELD}>Block</p>
            <div className="flex gap-2">
              <button type="button" onClick={onDuplicateBlock} className={`${BTN} flex-1`}>
                Duplicate
              </button>
              <button
                type="button"
                onClick={onDeleteBlock}
                className="flex-1 rounded-full border border-red-200 px-3 py-1 font-inter text-xs text-red-500 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Background view ──────────────────────────────────────────────────────
  if (view === 'background') {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-beige bg-white px-4 py-3">
          <button
            type="button"
            onClick={() => setView('slide')}
            className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-marine-green"
          >
            ← Slide
          </button>
          <span className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-300">/</span>
          <span className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-700">
            Background
          </span>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-beige bg-cream-alt px-3 py-2">
          {(['library', 'upload', 'generate'] as BgTab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setBgTab(t)}
              className={`flex-1 rounded-lg px-2 py-1.5 font-inter text-xs font-semibold transition ${
                bgTab === t
                  ? 'bg-marine-green text-white shadow-sm'
                  : 'text-slate-500 hover:bg-white'
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {bgTab === 'library' && (
            <div className="p-3">
              <PhotoslopLibraryPanel active onPick={pickBg} showUploadButton={false} />
            </div>
          )}
          {bgTab === 'upload' && <UploadTab onPick={pickBg} />}
          {bgTab === 'generate' && (
            <div className="p-3">
              <GeneratePanel onPick={pickBg} onError={(msg) => alert(msg)} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Slide view ────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">

      {/* Slide actions */}
      <div className="space-y-3 rounded-xl border border-beige bg-white p-4">
        <p className={FIELD}>Slide</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={flipSlide}
            className="rounded-full border border-marine-green/40 bg-marine-green/10 px-3 py-1 font-inter text-xs font-semibold text-marine-green hover:bg-marine-green hover:text-white"
            title="Mirror every block to the opposite side (left ↔ right)"
          >
            Flip ⇄
          </button>
          <button type="button" onClick={onDuplicateSlide} className={BTN}>
            Duplicate
          </button>
          <button
            type="button"
            onClick={onDeleteSlide}
            className="rounded-full border border-red-200 px-3 py-1 font-inter text-xs text-red-500 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Background — clickable thumbnail that drills into picker */}
      <div className="space-y-2 rounded-xl border border-beige bg-white p-4">
        <p className={FIELD}>Background</p>
        <button
          type="button"
          onClick={() => setView('background')}
          className="group relative w-full overflow-hidden rounded-lg border border-beige transition hover:border-marine-green"
        >
          {slide.bg?.src ? (
            <img
              src={slide.bg.src}
              alt=""
              className="aspect-[16/9] w-full object-cover"
            />
          ) : (
            <div
              className="flex aspect-[16/9] w-full items-center justify-center text-[10px] text-slate-400"
              style={{ backgroundColor: slide.bg?.color ?? '#0D0D14' }}
            >
              {slide.bg?.color ?? 'No image'}
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/30">
            <span className="rounded-full bg-white px-3 py-1 font-inter text-xs font-semibold text-slate-800 opacity-0 shadow transition group-hover:opacity-100">
              Change image
            </span>
          </div>
        </button>
        {slide.bg?.src && (
          <p className="truncate font-dank-mono text-[9px] text-slate-400">
            {slide.bg.src.split('/').pop()}
          </p>
        )}
      </div>

      {slide.bg?.src && (
        <BackgroundAdjustControls
          bg={slide.bg}
          onChange={(bg) => onChange({ ...slide, bg })}
        />
      )}

      {/* Background opacity + gradient scrim */}
      <div className="space-y-3 rounded-xl border border-beige bg-white p-4">
        <p className={FIELD}>Overlay</p>

        {slide.bg?.src && (
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="font-inter text-[10px] text-slate-400">Image opacity</p>
              <span className="font-dank-mono text-[10px] text-slate-500">{bgOpacityPct}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={bgOpacityPct}
              onChange={(e) => setBgOpacity(Number(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-marine-green"
            />
            <div className="mt-1 flex justify-between font-inter text-[9px] text-slate-300">
              <span>hidden</span>
              <span>full</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-beige pt-3">
          <p className="font-inter text-[10px] text-slate-400">Gradient scrim</p>
          <button
            type="button"
            onClick={toggleScrim}
            className={`relative h-5 w-9 rounded-full transition-colors ${
              scrimOn ? 'bg-marine-green' : 'bg-slate-200'
            }`}
            title={scrimOn ? 'Turn off gradient' : 'Turn on gradient'}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                scrimOn ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {scrimOn && (
          <>
            <div>
              <p className="mb-1.5 font-inter text-[10px] text-slate-400">Direction</p>
              <div className="inline-grid grid-cols-3 gap-1">
                {DIR_GRID.map((row, ri) =>
                  row.map((dir, ci) =>
                    dir === null ? (
                      <div key={`${ri}-${ci}`} className="h-7 w-7" />
                    ) : (
                      <button
                        key={dir}
                        type="button"
                        onClick={() => setDirection(dir)}
                        title={dir}
                        className={`flex h-7 w-7 items-center justify-center rounded text-sm transition-colors ${
                          scrim.direction === dir
                            ? 'bg-marine-green text-white'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        {DIR_ARROW[dir]}
                      </button>
                    ),
                  ),
                )}
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <p className="font-inter text-[10px] text-slate-400">Scrim intensity</p>
                <span className="font-dank-mono text-[10px] text-slate-500">{intensity}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={intensity}
                onChange={(e) => setIntensity(Number(e.target.value))}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-marine-green"
              />
              <div className="mt-1 flex justify-between font-inter text-[9px] text-slate-300">
                <span>none</span>
                <span>full</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Text tone */}
      <div className="space-y-3 rounded-xl border border-beige bg-white p-4">
        <p className={FIELD}>Text tone</p>
        <div className="flex gap-2">
          {(['light', 'dark'] as TextColor[]).map((c) => {
            const active = (slide.textColor ?? 'light') === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => onChange({ ...slide, textColor: c })}
                className={`flex-1 rounded-full border px-3 py-1 font-inter text-xs ${
                  active
                    ? 'border-marine-green bg-marine-green text-white'
                    : 'border-beige text-slate-600 hover:border-marine-green'
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function UploadTab({ onPick }: { onPick: (path: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-col items-center gap-4 p-6">
      <p className="max-w-xs text-center font-inter text-xs text-slate-500">
        Upload an image from your computer. It lands in your photoslop uploads folder and applies as
        the slide background.
      </p>
      <label className="cursor-pointer rounded-full bg-marine-green px-5 py-2.5 font-inter text-sm font-semibold text-white hover:bg-marine-green/90">
        {busy ? 'Uploading…' : 'Choose image'}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          disabled={busy}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setBusy(true);
            try {
              const fd = new FormData();
              fd.append('file', f);
              const res = await fetch('/api/photoslop/upload', { method: 'POST', body: fd });
              if (res.ok) {
                const data = (await res.json()) as { path?: string };
                if (data.path) onPick(data.path);
              } else {
                alert(`Upload failed (${res.status})`);
              }
            } finally {
              setBusy(false);
              e.target.value = '';
            }
          }}
        />
      </label>
    </div>
  );
}
